'use client';
import React from 'react';
import { CreditCard, Calendar, AlertCircle, CheckCircle } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';

interface Obligation {
  id: string;
  type: string;
  period: string;
  dueDate: string;
  amount: number;
  amountLRD: number;
  status: 'overdue' | 'pending' | 'paid' | 'filed';
  daysOverdue?: number;
  penalty?: number;
}

// Backend integration point: replace with API call to /api/taxpayer/:tin/obligations
const obligations: Obligation[] = [
  {
    id: 'obl-001',
    type: 'Goods & Services Tax (GST)',
    period: 'Q1 2026 (Jan–Mar)',
    dueDate: '04/30/2026',
    amount: 2140.0,
    amountLRD: 342400.0,
    status: 'overdue',
    daysOverdue: 28,
    penalty: 214.0,
  },
  {
    id: 'obl-002',
    type: 'Corporate Income Tax',
    period: 'FY 2025 Annual Return',
    dueDate: '03/31/2026',
    amount: 2680.0,
    amountLRD: 428800.0,
    status: 'overdue',
    daysOverdue: 58,
    penalty: 536.0,
  },
  {
    id: 'obl-003',
    type: 'Property Tax',
    period: 'FY 2026 — Commercial Property',
    dueDate: '06/30/2026',
    amount: 780.0,
    amountLRD: 124800.0,
    status: 'pending',
  },
  {
    id: 'obl-004',
    type: 'Withholding Tax',
    period: 'April 2026',
    dueDate: '05/15/2026',
    amount: 0,
    amountLRD: 0,
    status: 'paid',
  },
];

interface ObligationCardsProps {
  onPay: (id: string) => void;
}

export default function ObligationCards({ onPay }: ObligationCardsProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-4 gap-3 mb-4">
      {obligations.map((obl) => (
        <div
          key={obl.id}
          className="rounded-xl p-4 flex flex-col gap-3 transition-all duration-200 hover:brightness-110"
          style={{
            backgroundColor: 'var(--dark-card)',
            border: `1px solid ${
              obl.status === 'overdue' ?'rgba(220,38,38,0.35)'
                : obl.status === 'paid' ?'rgba(74,222,128,0.2)' :'var(--dark-border)'
            }`,
            background:
              obl.status === 'overdue' ?'rgba(153,27,27,0.12)'
                : obl.status === 'paid' ?'rgba(22,101,52,0.08)' :'var(--dark-card)',
          }}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold truncate" style={{ color: 'var(--dark-text)' }}>
                {obl.type}
              </p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>
                {obl.period}
              </p>
            </div>
            <StatusBadge variant={obl.status} size="sm" />
          </div>

          <div>
            {obl.status === 'paid' ? (
              <div className="flex items-center gap-2">
                <CheckCircle size={18} style={{ color: '#4ade80' }} />
                <span className="text-sm font-semibold" style={{ color: '#4ade80' }}>
                  Paid in full
                </span>
              </div>
            ) : (
              <>
                <p className="text-2xl font-bold tabular-nums" style={{ color: obl.status === 'overdue' ? '#fca5a5' : 'var(--dark-text)' }}>
                  ${obl.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </p>
                <p className="text-xs font-mono-data" style={{ color: 'var(--dark-muted)' }}>
                  LRD {obl.amountLRD.toLocaleString()}
                </p>
              </>
            )}
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--dark-muted)' }}>
              <Calendar size={11} />
              Due: {obl.dueDate}
            </div>
            {obl.daysOverdue && (
              <div className="flex items-center gap-1.5 text-xs" style={{ color: '#fca5a5' }}>
                <AlertCircle size={11} />
                {obl.daysOverdue} days overdue · Penalty: ${obl.penalty?.toLocaleString()}
              </div>
            )}
          </div>

          {obl.status !== 'paid' && (
            <button
              onClick={() => onPay(obl.id)}
              className="w-full py-2 rounded-lg text-xs font-semibold transition-all duration-150 active:scale-95 hover:opacity-90"
              style={{
                backgroundColor: obl.status === 'overdue' ? 'rgba(220,38,38,0.8)' : 'var(--accent)',
                color: obl.status === 'overdue' ? '#fff' : 'var(--accent-foreground)',
              }}
            >
              <CreditCard size={12} className="inline mr-1.5" />
              {obl.status === 'overdue' ? 'Pay Now — Overdue' : 'Pay Obligation'}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}